import { callRpc } from "./client";

export interface DadosPerfilUsuario {
  nomePerfil: string;
  telefonePerfil: string;
  telefonePerfilDdi?: string;
  emailPerfil: string;
  idImagem: string | number;
  urlImagem: string;
  doisFatoresAtivo: boolean;
}

interface RespostaPadrao {
  sucesso?: boolean;
  erro?: string;
  id?: string | number;
  url?: string;
}

interface RespostaConfiguracao2FA extends RespostaPadrao {
  secret?: string;
  qrCode?: string;
}

export interface PreferenciasNotificacoesUsuario {
  geral: boolean;
  chamadosNovos: boolean;
  chamadosEncerrados: boolean;
  estoqueBaixo: boolean;
  novaVenda: boolean;
  parcelasAtrasadas: boolean;
  confirmacaoPagamento: boolean;
  pedidosInsumosClientes: boolean;
  pedidosSuporteClientes: boolean;
  vencimentoInsumos: boolean;
  comprasNovas: boolean;
  /** Tickets: agendamentos e checklists do quadro de suporte. */
  tarefasSuporte: boolean;
  checklistsSuporte: boolean;
  /** Produção: formatação criada, cartão movimentado e etapa concluída. */
  formatacaoNova: boolean;
  producaoMovimentacao: boolean;
  producaoEtapaConcluida: boolean;
  producaoPrazoProximo: boolean;
  producaoEmAtraso: boolean;
  /** Plataforma: protocolo de retirada/recebimento registrado. */
  protocoloRegistrado: boolean;
  /** Atendimento: mensagens dos canais de conversa. */
  mensagensAtendimento: boolean;
  chatInterno: boolean;
  avaliacoesAtendimento: boolean;
  /** Vendas: ciclo comercial completo. */
  orcamentosNovos: boolean;
  pedidosCliente: boolean;
  entregasVenda: boolean;
  cobrancasVenda: boolean;
  fichaTecnica: boolean;
  documentosFiscais: boolean;
  /** Produção: telas criadas depois do catálogo inicial. */
  impressoesDtf: boolean;
  avariasProducao: boolean;
  comissoesProducao: boolean;
  expedicaoProducao: boolean;
  checklistsProducao: boolean;
  oficinasServicos: boolean;
  /** Estoque e compras. */
  inventarioEstoque: boolean;
  transferenciasEstoque: boolean;
  cotacoesCompras: boolean;
  /** Financeiro. */
  despesasVencendo: boolean;
  movimentoCaixa: boolean;
}

export type ChaveNotificacaoDisponivel = Exclude<keyof PreferenciasNotificacoesUsuario, "geral">;

export type CanalNotificacao = "email" | "push" | "whatsapp";

export type CanaisNotificacao = Record<CanalNotificacao, boolean>;

/** Preferência de cada notificação em cada canal. */
export type PreferenciasPorCanal = Record<ChaveNotificacaoDisponivel, CanaisNotificacao>;

export interface ConfiguracaoNotificacoesUsuario {
  preferencias: PreferenciasNotificacoesUsuario;
  canais: CanaisNotificacao;
  porCanal: PreferenciasPorCanal;
  somAtivo: boolean;
  opcoesDisponiveis: ChaveNotificacaoDisponivel[];
}

interface RespostaPreferencias extends RespostaPadrao {
  preferencias?: PreferenciasNotificacoesUsuario;
  canais?: CanaisNotificacao;
  porCanal?: PreferenciasPorCanal;
  somAtivo?: boolean;
  opcoesDisponiveis?: ChaveNotificacaoDisponivel[];
}

export async function obterDadosPerfilUsuario(): Promise<DadosPerfilUsuario | null> {
  const res = await callRpc<DadosPerfilUsuario | null>("obterDadosPerfilUsuario");
  return res ?? null;
}

export async function salvarPerfilUsuario(
  nome: string,
  telefone: string,
  telefoneDdi: string
): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("salvarNomeUsuario", nome, telefone, telefoneDdi);
}

export async function salvarAvatarUsuario(base64: string): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("salvarAvatarUsuario", base64);
}

export async function removerAvatarUsuario(): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("removerAvatarServidor");
}

export async function alterarSenhaUsuario(
  senhaAtual: string,
  senhaNova: string
): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("alterarSenhaUsuario", senhaAtual, senhaNova);
}

export async function iniciarConfiguracao2FA(): Promise<RespostaConfiguracao2FA> {
  return callRpc<RespostaConfiguracao2FA>("iniciarConfiguracao2FA");
}

export async function confirmarAtivacao2FA(codigo: string): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("confirmarAtivacao2FA", codigo);
}

export async function desativarDoisFatores(senha: string): Promise<RespostaPadrao> {
  return callRpc<RespostaPadrao>("desativarDoisFatores", senha);
}

export async function obterPreferenciasNotificacoesUsuario(): Promise<ConfiguracaoNotificacoesUsuario> {
  const resposta = await callRpc<RespostaPreferencias>("obterPreferenciasNotificacoesUsuario");
  const preferencias: PreferenciasNotificacoesUsuario = resposta.preferencias ?? {
    geral: true,
    chamadosNovos: true,
    chamadosEncerrados: true,
    estoqueBaixo: true,
    novaVenda: true,
    parcelasAtrasadas: true,
    confirmacaoPagamento: true,
    pedidosInsumosClientes: true,
    pedidosSuporteClientes: true,
    vencimentoInsumos: true,
    comprasNovas: true,
    tarefasSuporte: true,
    checklistsSuporte: true,
    formatacaoNova: true,
    producaoMovimentacao: true,
    producaoEtapaConcluida: true,
    producaoPrazoProximo: true,
    producaoEmAtraso: true,
    protocoloRegistrado: true,
    mensagensAtendimento: true,
    chatInterno: true,
    avaliacoesAtendimento: true,
    orcamentosNovos: true,
    pedidosCliente: true,
    entregasVenda: true,
    cobrancasVenda: true,
    fichaTecnica: true,
    documentosFiscais: true,
    impressoesDtf: true,
    avariasProducao: true,
    comissoesProducao: true,
    expedicaoProducao: true,
    checklistsProducao: true,
    oficinasServicos: true,
    inventarioEstoque: true,
    transferenciasEstoque: true,
    cotacoesCompras: true,
    despesasVencendo: true,
    movimentoCaixa: true,
  };
  const chaves = Object.keys(preferencias).filter((chave) => chave !== "geral") as ChaveNotificacaoDisponivel[];
  const porCanalPadrao = Object.fromEntries(
    chaves.map((chave) => [
      chave,
      { email: preferencias[chave] !== false, push: false, whatsapp: false },
    ])
  ) as PreferenciasPorCanal;

  return {
    preferencias,
    canais: resposta.canais ?? { email: preferencias.geral !== false, push: false, whatsapp: false },
    porCanal: resposta.porCanal ?? porCanalPadrao,
    somAtivo: resposta.somAtivo !== false,
    opcoesDisponiveis: resposta.opcoesDisponiveis ?? [],
  };
}

export async function salvarPreferenciasNotificacoesUsuario(payload: {
  canais: CanaisNotificacao;
  porCanal: PreferenciasPorCanal;
  somAtivo: boolean;
}): Promise<RespostaPreferencias> {
  return callRpc<RespostaPreferencias>("salvarPreferenciasNotificacoesUsuario", payload);
}
